/**
 * @fileoverview Shared constants used across the CasebookMap application.
 * Centralizes magic strings, configuration values, and enums for maintainability.
 * @module constants
 */


/* special advanced search debug queries:
debugAddressPoints
*/

/**
 * Application version and contact information.
 * @constant {Object}
 * @property {string} CbmVersionString - Semantic version string
 * @property {string} CbmVersionDate - Date of the current version
 * @property {string} CbmContact - Author name and email
 */
export const VERSION_INFO = {
    CbmVersionString: 'v7.0',
    CbmVersionDate: '10/15/26',
    CbmContact: 'Jesse Reichler (jessereichler@gmail.com)',
}



/**
 * Correction factor for EPSG:3857 distances at NYC latitude.
 * 1000 "meters" in EPSG:3857 ≈ 760 real meters in NYC.
 * @constant {number}
 */
// 1000 "meters" in EPSG:3857 ≈ 760 real meters in NYC.
export const MERCATOR_NYC_DISTANCE_FACTOR = 0.760;

/**
 * Map coordinate modes.
 * @enum {string}
 */
export const MAP_MODES = {
    GEOGRAPHIC: 'geographic',
    RASTER: 'raster'
};

/**
 * POI coordinate systems for raster map mode.
 * Defines how POI geometry coordinates are interpreted before conversion to the
 * internal raster pixel system (x: 0..imageWidth, y: -imageHeight..0).
 * @enum {string}
 */
export const RASTER_POI_COORDS = {
    NORMALIZED: 'normalized',  // 0,0 = top-left, 1,1 = bottom-right
    PIXEL: 'pixel',            // 0,0 = top-left, imageWidth,imageHeight = bottom-right
};

/** @constant {string} Default localStorage key prefix for app-wide settings */
export const DEFAULT_APP_STORAGE_PREFIX = 'cbmap:';

/** @constant {string} Path to the help HTML file */
export const HELP_HTML_URL = 'data/help/help.html';

/** @constant {string} Path to the imageMapDir */
export const IMAGE_MAP_PATH = 'data/imageMaps/';

/** @constant {Array<string>} Image list keys for the user icon picker */
// export const USER_ICON_PICKER_IMAGELIST = ['user', 'icons'];
export const USER_ICON_PICKER_IMAGELIST = ['user'];




/**
 * Search type identifiers used to distinguish different search modes.
 * @enum {string}
 */
export const SEARCH_TYPES = {
    WHITE: 'white',
    YELLOW: 'yellow',
    ADVANCED: 'advanced',
    REVERSE: 'reverse',
    BOOKMARKS: 'bookmarks',
    SAVEDSEARCH: 'savedsearch',
    HISTORY: 'history',
    LOGPLAY: 'logplay'
};




/**
 * Layer visibility configuration for the overlay control.
 * @constant {Array<Object>}
 */
export const OVERLAYCONTROL_LAYER_NAMES = [
    { id: SEARCH_TYPES.WHITE, name: 'White Pages' },
    { id: SEARCH_TYPES.YELLOW, name: 'Yellow Pages' },
    { id: SEARCH_TYPES.REVERSE, name: 'Reverse Directory' },
    { id: SEARCH_TYPES.ADVANCED, name: 'Advanced Search' },
    { id: SEARCH_TYPES.BOOKMARKS, name: 'Bookmarks' },
    { id: SEARCH_TYPES.HISTORY, name: 'History' }
];

/**
 * Identifiers for auxiliary (non-search-result) overlay layers on the map.
 * These layers handle highlighting, region shading, and anchor markers.
 * @constant {Object.<string, string>}
 * @property {string} HIGHLIGHT - Layer for highlighted/hovered POI features
 * @property {string} REGION - Layer for region boundary shading
 * @property {string} ANCHOR - Layer for the anchor marker position
 * @property {string} ANCHOR_POI - Layer for POI associated with the anchor
 */
export const MAP_AUX_OVERLAY_LAYERS = {
    HIGHLIGHT: 'highlight',
    REGION: 'region',
    ANCHOR: 'anchor',
    ANCHOR_POI: 'anchorPoi',
    DEBUG: 'debug'
};






/**
 * Human-readable labels for search types, used in UI display.
 * @constant {Object.<string, string>}
 */
export const SEARCH_TYPE_LABELS = {
    [SEARCH_TYPES.WHITE]: 'White Pages',
    [SEARCH_TYPES.YELLOW]: 'Yellow Pages',
    [SEARCH_TYPES.ADVANCED]: 'Advanced',
    [SEARCH_TYPES.REVERSE]: 'Reverse Directory',
    [SEARCH_TYPES.BOOKMARKS]: 'Bookmarks',
    [SEARCH_TYPES.SAVEDSEARCH]: 'Saved Searches',
    [SEARCH_TYPES.HISTORY]: 'History',
    [SEARCH_TYPES.LOGPLAY]: 'Log Play'
};


/**
 * POI type identifiers used to classify items in the data and search results.
 * Database types (YELLOW, PERSON, ACTION, POI) correspond to records in the POI database.
 * Non-database types (SEARCH, HEADER, POISTUB) are used for UI and serialization purposes.
 * @enum {string}
 */
export const PTYPES = {
    YELLOW: 'yellow',
    PERSON: 'person',
    ACTION: 'action',
    POI: 'poi', // in db generated by nybest, etc. for things like SUBWAYS
    //
    SEARCH: 'search',
    HEADER: 'header',
    //
    POISTUB: 'poiStub',
    CUSTOM: 'custom',
    PDFLEAD: 'pdfLead',
}


/**
 * Subset of PTYPES values that correspond to actual records in the POI database.
 * Used to distinguish database-backed items from UI-only item types.
 * @constant {Array<string>}
 */
// a list of ptype values that signify the result is in the poi DB
export const PTYPES_POIDB = [PTYPES.YELLOW, PTYPES.PERSON, PTYPES.ACTION, PTYPES.POI];



/**
 * Default configuration values for search behavior and UI limits.
 * @constant {Object}
 * @property {number} MIN_QUERY_LENGTH - Minimum characters required to trigger a search
 * @property {number} RESULT_LIMIT_DEFAULT_INITIAL_DB_SEARCH - Default maximum results for incremental search
 * @property {number} VIEW_SAVE_DEBOUNCE_MS - Debounce delay before saving map view state to localStorage
 * @property {number} RESULT_LIMIT_HISTORY_BEFORE_AUTO_TRUNCATE - Maximum history entries before automatic truncation
 * @property {number} SINGLE_CLICK_DELAY_MS - Delay in ms before a single click is confirmed (to distinguish from double-click)
 * @property {number} STATUS_TOAST_DURATION_MS - Duration in ms to display status toast notifications
 * @property {number} DELAYED_MESSAGE_TIMEOUT_MS - Timeout in ms before showing a delayed loading message
 * @property {number} LIVE_SEARCH_SAVE_DELAY_MS - Debounce delay in ms before persisting live search state
 * @property {number} INACTIVE_LAYER_OPACITY - Default opacity (0-1) for inactive search result layers
 * @property {number} OUTSIDE_REGION_OPACITY - Default opacity (0-1) for areas outside the selected region
 * @property {string} DEFAULT_MARKER_STYLE_OLD - Legacy default marker style identifier
 * @property {string} DEFAULT_MARKER_STYLE - Current default marker style identifier
 */
export const MAPAPP_DEFAULTS = {
    MIN_QUERY_LENGTH: 1,
    // # see also RESULT_MANAGER_CONSTANTS.NO_LIMIT_RESULT_COUNT below
    RESULT_LIMIT_DEFAULT_INITIAL_DB_SEARCH: 500,
    RESULT_LIMIT_DEFAULT_INITIAL_DB_SEARCH_ADVANCED_ENTER: 1000,
    RESULT_LIMIT_HISTORY_BEFORE_AUTO_TRUNCATE: 200,
    VIEW_SAVE_DEBOUNCE_MS: 500,
    SINGLE_CLICK_DELAY_MS: 250, // claude suggests 250 but it feels sluggish
    STATUS_TOAST_ERROR_DURATION_MS: 5000,
    STATUS_TOAST_WARNING_DURATION_MS: 2000,
    STATUS_TOAST_DEFAULT_DURATION_MS: 3000,
    STATUS_TOAST_LONG_DURATION_MS: 5000,
    DELAYED_MESSAGE_TIMEOUT_MS: 2000,
    LIVE_SEARCH_SAVE_DELAY_MS: 2000,
    TIME_BETWEEN_PLAY_LOG_TIME_HEADERS_IN_SECONDS: 60 * 5,
    SAVE_LOG_STATE_FREQUENCY_SECONDS: 5,
    INACTIVE_LAYER_OPACITY: 0.60,
    OUTSIDE_REGION_OPACITY: 0.75,
    DEFAULT_MARKER_STYLE: 'Orange Teardrop',
    // new world overrideable options
    REGION_BOUNDARY_LINE_WIDTH: 3,
    //REGION_BOUNDARY_DIM_OPACITY: 0.1,
    REGION_BOUNDARY_LINE_DASH_WIDTH: 8,
    MAP_MARKER_SCALE: 1.3,
    MAP_CANVAS_BACKGROUND_COLOR: "#8caac6",
    MAP_HIGHLIGHT_COLOR_1: "#ffd7004d",
    MAP_HIGHLIGHT_COLOR_2: "#ffa500e6",
    MAP_ANCHOR_COLOR_1: "#007bff33",
    MAP_ANCHOR_COLOR_2: "#0056b3e6",
    MAP_FLASH_COLOR_1: "#0056b3e6",
    MAP_FLASH_COLOR_2: "#ffd7004d",
    ZOOMIN_SCALE: 10,
    INITIAL_MAP_ZOOM_SCALE: 1.5,
    // fallback m3u playlists merged into the Music Player when a world's worldConfig has no
    // defaultMusicPlaylists; entries are {label, url} pointing at .m3u files (relative urls resolve
    // against the world config path). Empty by default — worlds supply their own.
    DEFAULT_MUSIC_PLAYLISTS: [],
    // Tile layer preload depth: Infinity makes OpenLayers proactively load all lower-zoom
    // ancestor tiles for the current view, so zooming OUT always has something resident to
    // draw instead of flashing blank (each ancestor level is 1/4 the tiles, so cost is small).
    TILE_LAYER_PRELOAD: Infinity,
    // Per-source in-memory tile cache high-water mark, in tiles (OpenLayers default is 512).
    // A HiDPI viewport is ~30-60 tiles per zoom level; 4096 keeps many zoom levels resident
    // so repeated zoom in/out over the same area is served from memory instead of refetching
    // (pmtiles range-request tiles are effectively uncacheable by the browser, so OL's own
    // cache is the only client-side cache those worlds have).
    TILE_SOURCE_CACHE_SIZE: 4096
};


// ==========================================
// MAP EFFECTS
// ==========================================

/**
 * CSS filter effect definitions for map tile layer visual adjustments.
 * Each entry defines a named effect with its corresponding CSS filter string.
 * @constant {Array<Object>}
 * @property {string} id - Unique identifier for the effect
 * @property {string} name - Human-readable display name
 * @property {string} filter - CSS filter value to apply to the map tile layer
 */
export const MAP_EFFECTS = [
    { id: 'none',          name: 'Normal',          filter: 'none' },
    { id: 'grayscale',     name: 'Grayscale',     filter: 'grayscale(75%) brightness(0.9) contrast(0.8)' },
    { id: 'lowContrast',      name: 'Low Contrast',      filter: 'grayscale(10%) sepia(20%) brightness(0.9) contrast(0.85) saturate(0.85)' },
    { id: 'lowSaturation',   name: 'Low Saturation', filter: 'contrast(0.75) saturate(0.75)' },
    { id: 'dark',     name: 'Dark',     filter: 'brightness(0.8) contrast(1.2) saturate(1.2)' },
    { id: 'darker',         name: 'Darker',         filter: 'contrast(1.0) brightness(0.7) grayscale(50%) ' },
    { id: 'darkest',          name: 'Darkest',              filter: 'brightness(0.6) contrast(1.6)' },
];


/**
 * Z-index ordering constants for map layers, controlling draw order.
 * Higher values are rendered on top of lower values.
 * @constant {Object.<string, number>}
 */
export const MAP_LAYER_ZINDICES = {
    DEFAULT_GENERIC: 0,
    REGIONS: 100,
    BOOKMARKS: 50,
    //
    POI_HIGHLIGHT: 200,
    ANCHOR: 210,
}



/**
 * Field names for bookmark visual customization properties (label, style, color, pin, scale, callout, look).
 * @constant {Array<string>}
 * @see nynfuncs cache function
 */
// for saving bookmarks; see also nynfuncs cache function
export const CUSTOM_ITEM_FIELDS_VISUAL = ['userLabel', 'userStyle', 'userColor', 'userPin', 'userScale', 'userCallout', 'userLook'];
/**
 * Field names for all bookmark custom properties: visual fields plus user notes.
 * @constant {Array<string>}
 */
export const CUSTOM_ITEM_FIELDS_BOOKMARK = [...CUSTOM_ITEM_FIELDS_VISUAL, 'userNotes'];




/**
 * Field names for all custom item properties: bookmark fields plus timestamps.
 * @constant {Array<string>}
 */
export const CUSTOM_ITEM_FIELDS_ALL = [...CUSTOM_ITEM_FIELDS_BOOKMARK, 'timeStampB', 'timeStampH'];
//


/**
 * These are new fields we may inject in TEMPORARILY but which don't get saved
 */
export const SPECIAL_TEMP_ITEM_FIELDS = ['dNameCustom', 'image', 'customBase'];
//export const SPECIAL_TEMP_ITEM_FIELDS = [];
export const CUSTOM_ITEM_FIELDS_ALL_PLUSTEMPS = [...CUSTOM_ITEM_FIELDS_ALL, ...SPECIAL_TEMP_ITEM_FIELDS];
//


/**
 * Field names excluded when comparing items for duplicate detection during add operations.
 * @constant {Array<string>}
 */
export const FIELDS_IGNORE_FOR_DUPEADD = ['uid', 'timeStampB', 'timeStampH'];
/**
 * Field names used for presumptive duplicate matching when adding items.
 * If all these fields match between two items, they are considered duplicates.
 * @constant {Array<string>}
 */
export const FIELDS_PRESUMPTIVE_MATCH_FOR_DUPEADD = ['searchType', 'searchQuery', 'selectedRegion', 'regionCheckbox', 'dName', 'lead'];
//
/**
 * The field name that acts as a flag to enable or disable custom appearance on an item.
 * When this field is falsy, visual customization fields are ignored during rendering.
 * @constant {string}
 */
export const FIELD_ENABLE_CUSTOM_APPEARANCE = 'userLook';
/**
 * Custom appearance fields that are ignored when the custom appearance flag is disabled.
 * These fields remain stored on the item but have no visual effect unless userLook is enabled.
 * @constant {Array<string>}
 */
export const FIELDS_CUSTOMIZE_IGNORE_ON_DISABLED_CUSTOM_APPEARANCE = ['userStyle', 'userColor', 'userPin', 'userScale', 'userCallout'];






/**
 * Fields to serialize when saving item stubs to localStorage, keyed by search type.
 * Each search type preserves only the fields relevant to its storage needs.
 * @constant {Object.<string, Array<string>>}
 */
export const STUB_SERIALIZATION_FIELDS = {
    [SEARCH_TYPES.BOOKMARKS]: [...CUSTOM_ITEM_FIELDS_BOOKMARK, 'timeStampB'],
    [SEARCH_TYPES.SAVEDSEARCH]: ['timeStampB'],
    [SEARCH_TYPES.HISTORY]: ['timeStampH'],
}


export const FIELD_ISBOOKMARK = 'isBookmark';
export const FIELD_ISCUSTOMIZED = 'isCustomized';

/**
 * Fields to clear when removing customization from an item, keyed by search type.
 * Includes serialization fields plus any additional flags specific to each type.
 * @constant {Object.<string, Array<string>>}
 */
export const CLEAR_FIELDS = {
    [SEARCH_TYPES.BOOKMARKS]: [...STUB_SERIALIZATION_FIELDS[SEARCH_TYPES.BOOKMARKS], FIELD_ISBOOKMARK, FIELD_ISCUSTOMIZED],
    [SEARCH_TYPES.SAVEDSEARCH]: STUB_SERIALIZATION_FIELDS[SEARCH_TYPES.SAVEDSEARCH],
    [SEARCH_TYPES.HISTORY]: STUB_SERIALIZATION_FIELDS[SEARCH_TYPES.HISTORY],
}


//export const ADVANCED_SEARCH_FIELDS = ['dName', 'address', 'lead', 'pcat', 'userLabel', 'userNotes'];
export const ADVANCED_SEARCH_FIELDS = ['dName', 'address', 'lead', 'pcat', 'pcatg', 'userLabel', 'userNotes'];
export const ADVANCED_SEARCH_FIELDS_ANYWHERE = ['dName'];
export const ADVANCED_SEARCH_FIELDS_STRINGSTART = ['lead', 'pcatg'];


/**
 * Shared text string constants used across the application.
 * @constant {Object.<string, string>}
 */
export const TEXT_CONSTANTS = {
    UNLISTED_ADDRESS: "unlisted address",
    PDF_NO_FILE_LOADED: 'Load a case book pdf file using the button in the upper right, drop a pdf into this window, or click <a href="#" class="pdf-placeholder-load-link">here</a>.'
}


/**
 * The leads section's bookmark label, which the PDF reader opens in its bookmark sidebar when a pdf loads
 * (PdfViewer.expandBookmarkByLabel, first bookmark matching any entry): 'LEADS', and its translation in every
 * casebook message catalog as the engine prints the heading `# LEADS` -- the "Leads" section label (gettext
 * context "section label"), in capitals (lib/casebook/cblocale.translateSectionLabel). Until Oct 30 2026 only
 * 'LEADS' was opened, so a translated booklet's leads did not open on load. Compared through
 * PdfViewer.normalizeTitleLabel (lowercased), so case does not matter, and with or without a debug build's
 * trailing " [LEADS]" (the heading's id, added where the printed title differs). map.tests.LeadsSectionLabelsTests holds
 * this list to the catalogs both ways: a new or redrafted translation fails it until the list follows (then
 * `npm run build:map`).
 * @constant {Array<string>}
 */
export const LEADS_SECTION_LABELS = [
    'LEADS',
    'SPUREN',
    'PISTAS',
    'PISTES',
    'NYOMOK',
    'PISTE',
    '手がかり',
    'SPOREN',
    'LEDTRÅDAR',
    '线索',
    '線索',
];



/**
 * Default values for custom bookmark map marker styling (color, pin icon, scale).
 * @constant {Object}
 * @property {string} COLOR_DEFAULT - Default marker color (hex)
 * @property {string} PIN_DEFAULT - Default pin icon image path
 * @property {number} SCALE_DEFAULT - Default pin scale multiplier
 * @property {Object} PIN_ICON_SCALE - Min/max bounds for the pin icon scale slider
 */
// for custom bookmark styles, etc.
export const MAP_CUSTOMS = {
    COLOR_DEFAULT: '#11cc44',
    PIN_DEFAULT: 'images/jrmaki/marker.svg',
    SCALE_DEFAULT: 1.5,
    PIN_ICON_SCALE: {
        MIN: 50,
        MAX: 200,
    },
};





/**
 * Bootstrap Icons class names used in the UI.
 * @constant {Object.<string, string>}
 */
export const ICONS = {
    BOOKMARK: 'bi bi-star',
    BOOKMARK_FILLED: 'bi bi-star-fill',
    SAVEDSEARCH: 'bi bi-star',
    SAVEDSEARCH_FILLED: 'bi bi-star-fill',
    COPYCLIPBOARD: 'bi bi-clipboard2',
    EDIT: 'bi bi-pencil',
    EDIT_FILLED: 'bi bi-pen-fill',
    LAUNCH_SEARCH: 'bi bi-search',
    LAUNCH_ITEM: 'bi bi-crosshair',
    DELETE: 'bi bi-trash3',
    ANCHOR: 'bi bi-signpost',
    ANCHOR_FILLED: 'bi bi-signpost-fill',
    READLEAD: 'bi bi-book',
    READLEAD_FILLED: 'bi bi-book-fill',
    EXTRA_ACTION_MENU: 'bi bi-list'
};







/**
 * Auto-zoom style identifiers for map view fitting behavior.
 * @enum {string}
 */
export const AUTO_ZOOM_STYLE = {
    /** Zoom in to fit the extent */
    ZOOM_IN: 'zoomIn',
    /** Expand current view to include the extent without zooming in */
    EXPAND: 'expand',
    /** pan to */
    PAN_EXPAND: 'panZoomOutAsNeeded',
    /** No auto-zoom */
    NONE: 'none',
};



/**
 * Map configuration constants including projections, styling, and animation settings.
 * @constant {Object}
 */
export const MAP_CONSTANTS = {
    // Default map pin icon path
    DEFAULT_PIN_SRC: 'images/jr3/Marker-yellow.png',
    
    // Web Mercator max resolution at zoom level 0
    MAX_RESOLUTION: 156543.03392804097,
    // Projection identifiers
    PROJECTION: {
        WEB_MERCATOR: 'EPSG:3857',
        WGS84: 'EPSG:4326',
        NY_STATE_PLANE: 'EPSG:2263'
    },
    // Default padding for map fitting
    DEFAULT_PADDING: [0, 0, 0, 0],
    RESULT_LAYER_PADDING: [0, 0, 0, 0],
    // Popup positioning
    POPUP_OFFSET: [15, -25],
    // Pin scaling
    PIN_SCALE: {
        MIN: 0.001,
        MAX: 0.07,
        BASE_MULTIPLIER: 0.001,
        ZOOM_FACTOR: 1.25
    },
    // Animation duration in ms
    ANIMATION_DURATION_DEFAULT: 250,
    ANIMATION_DURATION_EXPAND: 500,
    ANIMATION_DURATION_ZOOMIN: 500,
    ANIMATION_DURATION_PAN_EXPAND: 500,

    // max distance for a click to say do a reverse search
    MAX_DIST_FOR_CLICK_SEARCH: 1000,

    // Region boundary styling
    REGION_BOUNDARY_COLOR: '#dc3545',

    // Default zIndex for searcher result layers
    DEFAULT_LAYER_ZINDEX: 0,
    ZINDEX_BUMP_PERSON: 20,
    ZINDEX_BUMP_YELLOW: 5,
    ZINDEX_BUMP_CUSTOM_BOOKMARK: 15,
    ZINDEX_BUMP_ACTION: 0,

    // for rendering svg markers of different sizes
    CUSTOM_MARKER_FIXED_WIDTH: 24,
    CUSTOM_MARKER_FIXED_HEIGHT: 24,
    CUSTOM_MARKER_BASE_SCALE:10,

    USE_PCAT_IMAGE_FOR_BOOKMARKED_YELLOW_PINS: false,
    PERSON_RADIUS_NORMAL: 5,
    PERSON_RADIUS_BOOKMARKED: 7,
    BOOKMARK_PIN_SCALING: 1.3,
    BOOKMARK_PIN_COLOR: '#fdad01',
    BOOKMARK_PERSON_COLOR: '#fdad01',
    BOOKMARK_ACTION_COLOR: '#fdad01',
    BOOKMARK_PCATG_ICON_COLOR: '#FF4444',
    MAX_ZOOMIN_TO_FIT_SCALE: 17,
    //
    PDFVIEWER_MOUSEWHEEL_COOLDOWN: 50,

    USER_BOOKMARK_NAME: 'User Bookmark',
};


/**
 * PMTiles archive loading knobs.
 * See PMTilesRangeOrWholeFileSource in map/OpenLayersMapManager.js.
 * @constant {Object}
 */
export const PMTILES_CONSTANTS = {
    // Hard ceiling on the "server ignored our Range header, so download the whole
    // archive once and slice it in memory" fallback. Production worlds ship 600+MB
    // .pmtiles archives, and buffering one of those is an instant multi-hundred-MB
    // allocation (a tab crash on mobile). Any archive small enough to be worth
    // buffering is far under this. Raising it is a deliberate developer act -- the
    // error thrown when it trips names this constant.
    WHOLE_FILE_FALLBACK_MAX_BYTES: 64 * 1024 * 1024,

    // Master switch for letting the browser HTTP cache hold .pmtiles range
    // responses. false == every range read uses cache:'no-store' (the old
    // unconditional behavior). true == read #1 still probes with 'no-store', and
    // only after a real 206 proves the origin byte-serves do later reads switch to
    // cache:'default'. Even when true this stays OFF for Chromium-on-Windows -- see
    // pmtilesBrowserHttpCacheAllowed() in OpenLayersMapManager.js. Set false to
    // revert the whole optimization without touching code.
    USE_BROWSER_HTTP_CACHE: true
};



/**
 * Color mapping for person markers by housing type.
 * @constant {Object.<string, string>}
 */
export const MAP_PERSON_COLORS = {
    A: '#7b0981',  // Purple for type A (apartments)
    D: '#864619',  // Brown for type D (duplexes)
    default: '#f3141d',  // Red for default
    stroke: 'rgba(0,0,0,0.6)'
};

/**
 * Color configuration for action-type POI markers on the map.
 * @constant {Object.<string, string>}
 */
export const MAP_ACTION_COLORS = {
    default: '#11FF11',
    stroke: 'rgba(0,0,0,0.6)'
}

/**
 * Color configuration for yellow-type (business/place) POI markers on the map.
 * @constant {Object.<string, string>}
 */
export const MAP_YELLOW_COLORS = {
    default: '#f7ff08',
};






/**
 * Lister configuration constants for display messages, limits, and CSS classes.
 * @constant {Object}
 */
export const RESULT_MANAGER_CONSTANTS = {
    /** Default message when no search has been performed */
    DEFAULT_EMPTY_RESULTS_MESSAGE: 'Start typing to search/filter.',
    /** Default message when search returns no results */
    DEFAULT_NO_RESULTS_MESSAGE: 'No matching results.',
    /** Effectively unlimited result count for filtering */
    NO_LIMIT_RESULT_COUNT: 99999,
    /** Default fields to search when filtering */
    DEFAULT_FILTER_FIELDS: ['label'],
    /** CSS class for selected result items */
    SELECTED_RESULT_CLASS: 'lister-selected',
    /** CSS class for action bar container */
    ACTION_BAR_CLASS: 'lister-action-bar',
    /** Label for re-randomize button when results have random+limit directives */
    RERUN_LABEL_RERANDOMIZE: 're-randomize',
    /** Label for show all button when results are truncated */
    RERUN_LABEL_SHOWALL: 'show all',
    /** Label for show all button when we are an internal searcher (no need to rerun a search just clear it) */
    RERUN_LABEL_CLEARFILTER: 'clear filter',
    /** Label for advanced searchers waiting on enter, etc. */
    RERUN_LABEL_WAITINGONENTER: 'or click here'
};





/**
 * Regular expressions used for parsing queries.
 * @constant {Object}
 */
export const REGEXES = {
    /** Regex for block queries with neighborhood code first (e.g., "MH123", "MH:123", "B:12"); new allows letter at end of number */
    NEIGHBORHOOD_FIRST: /^([a-zA-Z]{1,2})(?:[-:])?(\d{1,3}[a-zA-Z]?)$/,
    /** Regex for block queries with block number first (e.g., "123MH", "123:MH", "12:B") */
    NEIGHBORHOOD_LAST: /^(\d{1,3})(?:[-:])?([a-zA-Z]{1,2})$/
};



export const SIDEBAR_MIN_WIDTH = 100;
export const SIDEBAR_MAX_WIDTH = 4000;


/**
 * DOM element ID and CSS class string constants used for querySelector lookups.
 * Centralizes all DOM identifiers to avoid magic strings throughout the codebase.
 * @constant {Object.<string, string>}
 */
export const DOM_ELEMENTS = {
    // App / Sidebar
    ID_MAP: 'map',
    ID_APP_CONTAINER: 'app-container',
    ID_SIDEBAR: 'sidebar',
    ID_SIDEBAR_TOGGLE: 'sidebar-toggle',
    ID_SIDEBAR_TOGGLE_ICON: 'toggle-icon',
    ID_SIDEBAR_FULLWIDTH_TOGGLE: 'sidebar-fullwidth-toggle',
    ID_SIDEBAR_FULLWIDTH_TOGGLE_ICON: 'fullwidth-toggle-icon',
    ID_SIDEBAR_TITLE: 'sidebar-title',
    ID_SIDEBAR_RESIZE_HANDLE: 'sidebar-resize-handle',
    ID_SEARCH_TABS: 'searchTabs',
    ID_SEARCH_TABS_CONTENT: 'searchTabsContent',
    ID_SIDEBAR_ERROR: 'sidebarError',
    ID_SIDEBAR_ERROR_MESSAGE: 'sidebarErrorMessage',
    ID_SIDEBAR_ERROR_CLOSE: 'sidebarErrorClose',
    ID_STATUS_TOAST_CONTAINER: 'status-toast-container',

    // Main Panel (map container wrapper with optional tabs)
    ID_MAIN_PANEL: 'main-panel',
    ID_MAIN_PANEL_TABS: 'mainPanelTabs',
    ID_MAIN_PANEL_CONTENT: 'mainPanelContent',
    ID_MAIN_MAP_TAB: 'main-map-tab',
    ID_MAIN_MAP_PANE: 'main-map-pane',
    ID_MAIN_READING_TAB: 'main-reading-tab',
    ID_MAIN_READING_TAB_CLOSE: 'main-reading-tab-close',
    ID_MAIN_READING_PANE: 'main-reading-pane',
    ID_READING_PANE: 'reading-pane',
    ID_MAIN_BGMUSIC_TAB: 'main-bgmusic-tab',
    ID_MAIN_BGMUSIC_TAB_CLOSE: 'main-bgmusic-tab-close',
    ID_MAIN_BGMUSIC_PANE: 'main-bgmusic-pane',

    // Menu buttons
    ID_MENU_SETTINGS: 'menu-settings',
    ID_MENU_TOGGLE_PDF_READER: 'menu-toggle-pdf-reader',
    ID_MENU_BACKGROUND_MUSIC: 'menu-background-music',
    ID_MENU_WORLD_SELECT: 'menu-world-select',
    ID_MENU_HELP: 'menu-help',
    ID_MENU_ABOUT: 'menu-about',
    ID_LINK_SETTINGS_FROM_ADVANCED: 'link-settings-from-advanced',

    // Advanced search
    ID_ADVANCED_ENABLED_CONTENT: 'advanced-enabled-content',
    ID_ADVANCED_DISABLED_CONTENT: 'advanced-disabled-content',
    ID_ADVANCED_RESTRICT_REGION_CHECKBOX: 'advanced-restrict-region-checkbox',
    ID_ADVANCED_SORT_BY_ANCHOR_DISTANCE_CHECKBOX: 'advanced-sort-by-anchor-distance-checkbox',
    //
    CLASS_ADVANCEDONLY_CONTENT: 'advancedOnly',

    // Yellow pages
    ID_YELLOW_SEARCH_SELECT: 'yellow-search-select',
    ID_YELLOW_SEE_ALSO: 'yellow-see-also',

    // Settings form
    ID_SAVE_SETTINGS: 'saveSettings',
    ID_ENABLE_ADVANCED_SEARCH: 'enableAdvancedSearch',
    ID_THEME_SELECT: 'themeSelect',
    ID_INACTIVE_LAYER_OPACITY: 'inactiveLayerOpacityEl',
    ID_OUTSIDE_REGION_OPACITY: 'outsideRegionOpacityEl',
    ID_SHOW_EXTRA_PCAT_INFO: 'showExtraPcatInfo',
    ID_YELLOW_MARKER_STYLE: 'yellowMarkerStyle',
    ID_HIDE_LOG_PANEL: 'hideLogPanel',
    ID_SHOW_EXTRA_ACTION_BUTTONS: 'showExtraActionButtons',
    ID_STAY_ON_MAP_AFTER_READ: 'stayOnMapAfterReadButtonPress',
    ID_PDF_AUTO_HIGHLIGHT_ON_COPY: 'pdfAutoHighlightOnCopy',
    ID_PDF_POPUP_MEDIA_FILES: 'pdfPopupMediaFiles',
    ID_RIGHT_MARGIN_GUTTER: 'rightMarginGutterEl',
    ID_FIT_SIZE_ZOOM: 'fitSizeZoomEl',
    ID_PLAY_LOG_FONT_SIZE: 'playLogFontSizeEl',

    // Help viewer
    ID_HELP_VIEWER_CONTAINER: 'helpViewerContainer',
    ID_HELP_SEARCH: 'helpSearch',
    ID_HELP_SEARCH_CLEAR: 'helpSearchClear',
    ID_HELP_TOPIC_CONTENT: 'helpTopicContent',
    ID_HELP_TOPICS_LIST: 'helpTopicsList',

    // CSS classes (multi-file usage)
    CLASS_COLLAPSED: 'collapsed',
    CLASS_SIDEBAR_FULLWIDTH: 'sidebar-fullwidth',
    CLASS_LOADING: 'loading',
    CLASS_LOADED: 'loaded',
    CLASS_RESIZING: 'resizing',
    CLASS_ACTIVE_FILTER: 'active-filter',
    CLASS_LISTER_ITEM: 'lister-item',
    CLASS_LISTER_HEADER: 'lister-header',
    CLASS_LISTER_CONTENT: 'lister-content',
    CLASS_LISTER_COUNT: 'lister-count',
    CLASS_LISTER_EMPTY: 'lister-empty',
    CLASS_LISTER_CLEAR_ALL_BTN: 'lister-clear-all-btn',
    CLASS_YELLOW_SEE_ALSO_LINK: 'yellow-see-also-link',
    CLASS_SEE_ALSO_CONTENT: 'see-also-content',
    CLASS_ADVANCED_REGION_CHECKBOX_ROW: 'advanced-region-checkbox-row',
    CLASS_ADVANCED_ANCHOR_DISTANCE_CHECKBOX_ROW: 'advanced-anchor-distance-checkbox-row',
    CLASS_HELP_TOPIC_ITEM: 'help-topic-item',
    CLASS_ICON_PICKER_ITEM: 'icon-picker-item',
}





/**
 * Available UI theme definitions for the application.
 * Each theme applies CSS classes to the body element to change the visual style.
 * @constant {Array<Object>}
 * @property {string} id - Unique theme identifier
 * @property {string} label - Human-readable theme label for the settings dropdown
 * @property {string} cssClasses - CSS class name(s) applied to the body element
 */
// for theme control
export const THEMES = [
    {id: 'normal', label: 'normal theme (default)', cssClasses: 'themeNormal'},
    {id: 'compact', label: 'compact theme', cssClasses: 'themeCompact'},
    {id: 'dark', label: 'dark theme', cssClasses: 'themeDark'},
]


export const SEARCH_CONSTANTS = {
    DEFAULT_FUZZY_MATCH_THRESHOLD: 0.3,
    // the advanced search's `x ~= "pattern"` guard (jrdslquery.checkRegexPatternSafety; the help's
    // Advanced Search topic states these limits, keep it in step):
    // longest pattern accepted
    REGEX_PATTERN_MAX_LENGTH: 200,
    // the repeats one pattern may hold: an open-ended or wide repeat (*, +, {n,}, {n,m} with m-n >= 2)
    // costs 3, an optional one (?, {n,n+1}) 1. Measured over the 22839 nynoir names (Oct 2026): three
    // .* took 0.8 s for the whole search, four 6.4 s, five over a minute; eight .? 0.3 s, twelve 4.5 s
    REGEX_REPEAT_BUDGET: 9,
    // distinct patterns one search keeps compiled (a pattern taken from a field, e.g. dName ~= address,
    // is a different one per POI)
    REGEX_CACHE_MAX_ENTRIES: 1000,
}

export const PLAYLOG_CONSTANTS = {
    MAX_COPY_TEXT_LEN: 5000
}

// The five boroughs of New York City, a little padded, as [minLon, minLat, maxLon, maxLat] (EPSG:4326).
// 1940s.nyc (the city's 1940 tax photographs) covers only the city, so its geolocate action is offered
// for a place inside these bounds only -- never on another world (SHCD's London), nor for an off-map
// place of the NYC world (New Jersey) (decision D9, Oct 2026).
export const NYC_LONLAT_BOUNDS = [-74.30, 40.45, -73.65, 40.95];

export const WEB_URLS = {
    ADVANCED_SEARCH_NOTEBOOK_LM: 'https://notebooklm.google.com/notebook/65df42bb-6d69-45bd-99c7-ec86f605e312',
    ADVANCED_SEARCH_GOOGLE_DOC: 'https://docs.google.com/document/d/1sB5XuoODSduYAcvc_OkZbNi60nDKGfAXpR8lA9m4g4E/edit?usp=sharing',
    EXTERNAL_GEOLOC_1940sNYC: 'https://1940s.nyc/map/#${zoomLevel}/${latitude}/${longitude}/${rotation}',
    EXTERNAL_GEOLOC_GOOGLE_MAPS: 'https://www.google.com/maps/@${latitude},${longitude},${zoomLevel}z',
    EXTERNAL_GEOLOC_ARCGIS_AERIAL: 'https://www.arcgis.com/apps/instant/media/index.html?appid=e011fd05a86a4c09bd0b91fbc387f3eb&center=${longitude},${latitude}&level=${zoomLevel}'
}










/** @constant {boolean} Enable debug logging for tile loading, etc. */
export const DEBUG_CBM = false;
/** @constant {boolean} Enable debug logging for network requests */
export const DEBUG_NETWORK = false;
/** @constant {boolean} Enable debug logging for UI refresh cycles */
export const DEBUG_REFRESH = false;
/** @constant {boolean} Enable debug logging for tile source loading (PMTiles, MBTiles) */
export const DEBUG_TILES = false;

/**
 * Simulated network delay in milliseconds for testing slow connections.
 * Set to a non-zero value to add artificial latency to data loading.
 * @constant {number}
 */
// set to non-zero to simulate slow network loading
export const DEBUG_SIMULATE_NETWORK_DELAY_MS = 0;
